import * as React from 'react'
import { Icon } from 'antd'
import { Redirect, RouteProps, Switch } from 'react-router'
import { Link, Route } from 'react-router-dom'
import * as H from 'history'
import styled, { css } from 'styled-components'
import { getCurrentUser, checkAuthStatus, logout as logoutFn } from 'web-fns/dist/auth'
import { initGA, setPageView, setGaUserId } from 'web-ui/dist/helpers/ga-tracking/ga-tracking'
import Button from 'web-makeup/dist/Button'
import { FormattedMessage } from 'react-intl'

import RequestDetailsPage from '../RequestDetailsPage'

import FourOhFour from './../4xx'
import HomePage from './../Home'
import Producer from './../Producer'
import ProducerRequests from './../ProducerRequests'
import Producers from './../Producers'
import SideMenu from './../SideMenu'

const NavBanner = styled.div`
  display: grid;
  position: fixed;
  grid-template-areas: 'sidebar content';
  align-items: center;
  font-style: normal;
  font-weight: 800;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.9px;
  height: 2rem;
  z-index: 2;

  padding-left: 0.75rem;
  grid-template-columns: 100px 1fr;
  width: 100vw;
  max-width: 100%;
  background: #dba02d;
`
const NavLinksWrapper = styled.div`
  display: flex;
  width: 50%;
  font-weight: 600;
  height: 28.8px;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 0.96px solid rgba(255, 255, 255, 0.4);
`

const NavLink = styled.a<{ isHighLight: boolean }>`
  margin: auto 5px;
  text-align: center;
  color: ${({ isHighLight }) => (isHighLight ? '#A76B11' : 'white')};
  letter-spacing: -0.2px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  text-decoration-line: none;

  &:hover {
    color: ${({ isHighLight }) => (isHighLight ? '#A76B11' : 'white')};
    cursor: pointer;
    text-decoration-line: ${({ isHighLight }) => (isHighLight ? 'none' : 'underline')};
  }
`
const NavTitle = styled.p`
  margin: auto 5px;
  text-align: center;
  color: white;

  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
`

const Container = styled.main`
  display: grid;
  grid-template-areas: 'sidebar content';

  grid-template-columns: 250px 1fr;

  height: 100vh;
  width: 100vw;
  max-width: 100%;
`

const Sidebar = styled.aside`
  background: #202020;
  width: 250px;
  height: 100vh;
  top: 2rem;
  position: fixed;
  display: grid;
  padding-bottom: 1rem;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;
  grid-area: sidebar;
  overflow-y: auto;
  z-index: 1;
`

interface ContentProps {
  fullSize?: boolean
}

const Content = styled.article`
  grid-area: content;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;

  ${(props: ContentProps) =>
    props.fullSize
      ? ''
      : css`
          padding: 1.5rem;
        `};
`

type CustomRouteProps = RouteProps & {
  content: RouteProps['component']
  fullSize?: boolean
  path: string
}

const routes: CustomRouteProps[] = [
  {
    // Individual producer page
    path: '/producers/:producer_id',
    exact: true,
    content: Producer,
    fullSize: true,
  },
  {
    // filtering producers should be done with query strings
    path: '/producers',
    exact: true,
    content: Producers,
  },
  {
    // filtering requests should be done with query strings
    path: '/requests',
    exact: true,
    content: ProducerRequests,
  },
  {
    path: '/requests/:uuid',
    exact: true,
    content: RequestDetailsPage,
    fullSize: true,
  },
]

const UserInfo = styled.section`
  color: white;
  padding: 1.5rem;
  align-items: center;
  display: flex;
  justify-content: space-between;
`

export const LogoutButton = styled(Button as any)`
  background: transparent;
  border: none;
  color: white;
`

const Title = styled.span`
  border-left: 1px solid white;
  padding-left: 1rem;
  text-transform: uppercase;
  font-size: 1rem;
  color: white;
  line-height: 0.875rem;
`

const StyledLink = styled(Link)`
  padding: 1rem;
  display: grid;
  align-items: center;
  grid-column-gap: 2rem;
  grid-template-columns: 1.5rem auto;

  img {
    max-width: 37px;
  }
`

export const UserSection = ({
  name,
  history,
  logout = logoutFn,
}: {
  name: string
  history: H.History
  logout?: () => void
}) => (
  <UserInfo>
    <span>{name}</span>
    <LogoutButton
      data-qa="logout-cta"
      onClick={() => {
        logout()
        history.push('/')
      }}
    >
      <Icon type="logout" />
    </LogoutButton>
  </UserInfo>
)

const App: React.FC = () => {
  const currentUser = getCurrentUser()

  // @ts-ignore
  window.env.GA_TRACKER_ID && initGA(window.env.GA_TRACKER_ID)
  currentUser && currentUser.id && setGaUserId(currentUser.id)
  setPageView()

  return (
    <Switch>
      <Route
        exact
        path="/"
        render={() => (checkAuthStatus() ? <Redirect to="/dashboard" /> : <HomePage />)}
      />

      <Redirect from="/dashboard" to="/producers" />

      {routes.map((route, index) => (
        <Route
          key={index}
          path={route.path}
          exact={route.exact}
          render={props => {
            if (!checkAuthStatus()) {
              return <Redirect to="/" />
            }

            return (
              <>
                <NavBanner>
                  <NavTitle>
                    <FormattedMessage id="nav_banner.manager" />
                  </NavTitle>
                  <NavLinksWrapper>
                    <NavLink
                      href={window.env.SIDE_MENU_BASE_URL}
                      isHighLight={window.location.origin === window.env.SIDE_MENU_BASE_URL}
                    >
                      <FormattedMessage id="nav_banner.be_the_customer" />
                    </NavLink>
                  </NavLinksWrapper>
                </NavBanner>
                <Container>
                  <Sidebar>
                    <StyledLink to="/requests">
                      <img src="https://res.cloudinary.com/ninety-seconds/image/upload/f_auto,q_auto,c_thumb,fl_lossy,fl_progressive/-design-system/-logo-2023/iconmark/white.svg" />
                      <Title>Manager</Title>
                    </StyledLink>
                    <SideMenu {...props} />
                    <UserSection
                      name={currentUser ? currentUser.name : ''}
                      history={props.history}
                    />
                  </Sidebar>
                  <Content fullSize={route.fullSize}>
                    <Route component={route.content} />
                  </Content>
                </Container>
              </>
            )
          }}
        />
      ))}

      <Route component={FourOhFour} />
    </Switch>
  )
}

export default App



// WEBPACK FOOTER //
// ./src/components/App/App.tsx